/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import {StateService} from '@uirouter/core';
import * as _ from 'lodash';

import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {WorkspaceRegistryService} from '../../components/workspace-registry.service';
import {NetworkResource} from '../../components/api/resources/resource/network.resource';

@Injectable({
  providedIn: 'root'
})
export class AppShellStateService {
  networks: Record<string, any[]> = {
    ADVERTISING: [],
    DATA: [],
    POLITICAL: []
  };
  networksLoaded = false;
  app: string;
  apps: string[] = [];
  perms: any;
  anyPerms: any;
  sectionPerms: any;
  currentNetwork: any;
  gravatarSize = 22;
  workspaces: any;
  unread = 0;

  private readonly networksChangeListeners = new Set<() => void>();

  constructor(
    private readonly stateService: StateService,
    @Inject(NetworkResource) private readonly Network: any,
    private readonly localUserProfile: LocalUserProfile,
    private readonly LocalUserPermissions: LocalUserPermissions,
    private readonly LocalNetworkProfile: LocalNetworkProfile,
    private readonly WorkspaceRegistry: WorkspaceRegistryService
  ) {
  }

  get appNetworks(): any[] {
    return this.networks[this.app] || [];
  }

  /**
   * Subscribe to network list loads/updates. Used by the shell to refresh the
   * header under zoneless CD without ApplicationRef.tick() (which NG0100s
   * in-flight widgets).
   */
  onNetworksChange(listener: () => void): () => void {
    this.networksChangeListeners.add(listener);
    return () => this.networksChangeListeners.delete(listener);
  }

  bootstrap(): void {
    this.refreshShellState();
    this.workspaces = this.WorkspaceRegistry.workspaces;

    const localUser = this.localUserProfile.get();
    if (!localUser) {
      return;
    }

    this.Network.query({ids: localUser.networkTagIds, minimal: true, useNetworkTags: true}, (page: any) => {
      this.networks.ADVERTISING = _.filter(page.results, (n: any) => n.advertisingPlatform);
      this.networks.DATA = _.filter(page.results, (n: any) => n.dataPlatform);
      this.networks.POLITICAL = _.filter(page.results, (n: any) => n.politicalApp);
      if (!this.networks.POLITICAL.length && this.LocalNetworkProfile.get('politicalApp')) {
        const current = _.find(page.results, (n: any) => this.isCurrentNetwork(n));
        if (current) {
          this.networks.POLITICAL = [current];
        }
      }
      this.networksLoaded = true;
      this.syncCurrentNetwork();
      this.notifyNetworksChange();
    }, () => {
      this.networksLoaded = true;
      this.notifyNetworksChange();
    });
  }

  private notifyNetworksChange(): void {
    this.networksChangeListeners.forEach((listener) => listener());
  }

  refreshShellState(): void {
    this.app = this.localUserProfile.getCurrentApp();
    this.apps = _.filter(this.LocalUserPermissions.getApps(), (app: string) => app !== 'SELF_SERVICE');
    this.perms = this.LocalUserPermissions.getAllNetworkPermissions();
    this.anyPerms = this.LocalUserPermissions.getAnyVisibility();
    this.sectionPerms = this.LocalUserPermissions.getSectionPerms(this.app);
    this.syncCurrentNetwork();
  }

  syncCurrentNetworkFromAuthEvent(network: any): void {
    this.currentNetwork = network;
  }

  loadExternalLineItem(): void {
    const viewType = this.LocalNetworkProfile.isCalt() && this.LocalUserPermissions.canUseCaltLineItemViewType2() ? 2 : 1;
    this.stateService.go('app.adv.line-items.new', {viewType: viewType, full: viewType === 2});
  }

  switchApp(newApp: string): void {
    this.localUserProfile.set('app', newApp);
    this.stateService.go('app.home', null, {inherit: false, reload: true});
  }

  private isCurrentNetwork(n: any): boolean {
    return n.tagId === this.LocalNetworkProfile.getNetworkTag() || n.id === this.LocalNetworkProfile.get('networkId');
  }

  private syncCurrentNetwork(): void {
    if (!this.networks) {
      return;
    }
    const appNetworks = this.networks[this.app] || [];
    this.currentNetwork = _.find(appNetworks, (n: any) => this.isCurrentNetwork(n));
    this.gravatarSize = appNetworks.length > 1 ? 13 : 22;
  }
}
